/**
 * BankingProfileRoute — presentation-level gate for banking surfaces
 * (DC-1781; accepted design §5.1: the profile is NOT authorization).
 *
 * The server authorizes every /api/use-cases call independently (roles +
 * JWT org). This gate only keeps the banking SHELL from rendering for an
 * organization whose experience is dataset-first (PRE-4: no banking surface
 * reachable from core_dataset_v1), and reuses the existing AccessDenied
 * surface rather than inventing a second denial screen (§13.4).
 *
 * Profile pending renders a neutral state — never an optimistic flash of
 * either experience (§5.6).
 */

import React from 'react';
import { useExperienceProfile } from '../../hooks/useExperienceProfile';
import AccessDenied from '../auth/AccessDenied';

export const BankingProfileRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const profile = useExperienceProfile();

  if (profile.status === 'pending') {
    return (
      <div className="banking-profile-pending" aria-busy="true" role="status">
        <span className="sr-only">Resolving workspace…</span>
      </div>
    );
  }

  if (
    profile.status === 'resolved' &&
    profile.definition.identity === 'banking_use_case_v1'
  ) {
    return <>{children}</>;
  }

  // Core-profiled organizations, disabled, and unresolvable states all deny.
  // Unresolvable/disabled already renders the degraded shell at the landing;
  // a deep link here gets the canonical denial surface.
  return <AccessDenied />;
};

export default BankingProfileRoute;
